import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Header from "./Header";
import Footer from "./Footer";

export default function QuestionnaireFlow() {
  const navigate = useNavigate();
  const currentUser_id = Number(localStorage.getItem("user_id"));

  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const [isSelecting, setIsSelecting] = useState(false);
  const [answers, setAnswers] = useState([]);
  const [current, setCurrent] = useState(0);
  const [questions, setQuestions] = useState([]);
  const [pageTitle, setPageTitle] = useState("AI 性格测评");
  const [pageSubtitle, setPageSubtitle] = useState("");

  useEffect(() => {
    if (!currentUser_id) {
      navigate("/login");
      return;
    }

    const bootstrap = async () => {
      try {
        const [profileRes, questionnaireRes] = await Promise.all([
          fetch(`/api/profile/${currentUser_id}`),
          fetch("/api/public/questionnaire"),
        ]);
        const profileData = await profileRes.json();
        const questionnaireData = await questionnaireRes.json();

        if (profileRes.ok) {
          if (!profileData.profile_completed) {
            alert("请先完善资料再进行测评");
            navigate("/center/profile");
            return;
          }
          if (profileData.has_questionnaire) {
            navigate("/center/profile");
            return;
          }
        }

        if (questionnaireRes.ok) {
          setPageTitle(questionnaireData.title || "AI 性格测评");
          setPageSubtitle(questionnaireData.subtitle || "");
          setQuestions(questionnaireData.questions || []);
        } else {
          alert("加载测评题目失败");
        }
      } catch (err) {
        console.error("加载测评失败:", err);
        alert("加载测评失败");
      } finally {
        setPageLoading(false);
      }
    };

    bootstrap();
  }, [currentUser_id, navigate]);

  const currentQuestion =
    current >= 0 && current < questions.length ? questions[current] : null;

  const handleSelect = (option) => {
    if (isSelecting || loading || !currentQuestion) return;

    setIsSelecting(true);

    setAnswers((prev) => {
      const arr = [...prev];
      arr[current] = {
        ...option,
        question_id: currentQuestion.id,
        question: currentQuestion.q,
      };
      return arr;
    });

    if (current < questions.length - 1) {
      setTimeout(() => {
        setCurrent((prev) => Math.min(prev + 1, questions.length - 1));
        setIsSelecting(false);
      }, 300);
    } else {
      setIsSelecting(false);
    }
  };

  const handleSubmit = async () => {
    if (loading || isSelecting) return;

    if (answers.filter(Boolean).length < questions.length) {
      alert("请完成所有题目");
      return;
    }

    const totalScore = answers.reduce((sum, a) => sum + (a?.score || 0), 0);
    const reportText = `测评完成，总分 ${totalScore} 分`;

    localStorage.setItem("love_score", String(totalScore));
    setLoading(true);

    try {
      const res = await fetch("/api/fullProfile", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          user_id: currentUser_id,
          answers,
          score: totalScore,
          report: reportText,
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        alert(data.detail || "提交失败");
        return;
      }

      navigate("/result", {
        state: {
          score: totalScore,
          report: data.report || reportText,
        },
      });
    } catch (err) {
      console.error("接口请求异常:", err);
      alert("提交失败");
    } finally {
      setLoading(false);
    }
  };

  const progress = questions.length
    ? ((Math.min(current, questions.length - 1) + 1) / questions.length) * 100
    : 0;

  if (pageLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-gray-400">
        加载测评题目...
      </div>
    );
  }

  if (!questions.length) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-50 to-purple-50 flex flex-col">
        <Header />
        <div className="flex-1 flex items-center justify-center px-4">
          <div className="bg-white rounded-2xl shadow p-8 text-center max-w-md">
            <p className="text-gray-600">暂无可用的测评题目，请联系管理员配置。</p>
            <button
              onClick={() => navigate("/center")}
              className="mt-4 px-5 py-3 rounded-xl bg-pink-500 text-white"
            >
              返回个人中心
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 to-purple-50 flex flex-col">
      <Header />

      <div className="flex-1 flex items-center justify-center px-4 py-8">
        <div className="bg-white p-8 rounded-2xl shadow max-w-md w-full">
          {currentQuestion && (
            <div className="space-y-6 text-center">
              <div>
                <h2 className="text-xl font-bold">{pageTitle}</h2>
                {pageSubtitle ? (
                  <p className="text-sm text-gray-500 mt-2">{pageSubtitle}</p>
                ) : null}
              </div>

              <div>
                <div className="h-2 bg-gray-200 rounded">
                  <div
                    className="h-2 bg-pink-500 rounded transition-all"
                    style={{ width: `${progress}%` }}
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  第 {Math.min(current + 1, questions.length)} / {questions.length} 题
                </p>
              </div>

              <div className="p-6 bg-pink-50 rounded-xl">
                <p className="mb-4 font-semibold">{currentQuestion.q}</p>

                <div className="flex flex-col gap-3">
                  {currentQuestion.options.map((op, i) => (
                    <button
                      key={i}
                      onClick={() => handleSelect(op)}
                      disabled={isSelecting || loading}
                      className="w-full py-3 rounded-lg border hover:bg-pink-100 transition disabled:opacity-50"
                    >
                      {op.text}
                    </button>
                  ))}
                </div>
              </div>

              {current === questions.length - 1 && (
                <button
                  onClick={handleSubmit}
                  disabled={loading || isSelecting}
                  className="w-full bg-pink-500 text-white py-3 rounded-lg disabled:opacity-50"
                >
                  {loading ? "生成中..." : "生成我的报告"}
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
}
